import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Row, Col, Form, Card, Button, Breadcrumb, Table, Pagination, Badge, Modal,
} from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  createAuthority,
  getAuthorities,
  updateAuthority,
  getAllEmployees,
} from "../../../../api/onboarddeboard";

const Authority = () => {
  const token = localStorage.getItem("token");
  const [authorityName, setAuthorityName] = useState("");
  const [authorizedUserId, setAuthorizedUserId] = useState("");
  const [status, setStatus] = useState(true);
  const [errors, setErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authorityData, setAuthorityData] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("name");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [showEditWarningModal, setShowEditWarningModal] = useState(false);
  const [pendingEditAuthority, setPendingEditAuthority] = useState(null);
  const [originalValues, setOriginalValues] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await getAllEmployees(token);
      setUsers(res.data || []);
    } catch (err) {
      toast.error("Error loading users.");
    }
  }, [token]);

  const fetchAuthorities = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAuthorities(token);
      if (response && response.status === 200) {
        const normalized = (response.data || []).map((item) => ({
          id: item.authorityId,
          authorityName: item.authorityName,
          authorizedUserId: item.user?.id,
          authorizedUserFullName: item.user?.name || "",
          active: item.status,
          linkedEntriesCount: item.linkedEntriesCount || 0,
        }));
        setAuthorityData(normalized);
      } else {
        toast.error("Failed to fetch Authorities.");
      }
    } catch (err) {
      toast.error("Error loading Authorities. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUsers();
      fetchAuthorities();
    }
  }, [token, fetchUsers, fetchAuthorities]);

  useEffect(() => {
    let filtered = [...authorityData];
    if (searchQuery.trim()) {
      filtered = filtered.filter((item) =>
        item.authorityName.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }
    if (statusFilter !== "all") {
      const isActive = statusFilter === "active";
      filtered = filtered.filter((item) => item.active === isActive);
    }
    if (sortField === "name") {
      filtered.sort((a, b) => a.authorityName.localeCompare(b.authorityName));
    } else if (sortField === "status") {
      filtered.sort((a, b) => {
        if (a.active === b.active) return a.authorityName.localeCompare(b.authorityName);
        return a.active ? -1 : 1;
      });
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  }, [authorityData, searchQuery, statusFilter, sortField]);

  const validateDuplicatesLive = useCallback(() => {
    if (!authorityData || authorityData.length === 0) return;
    if (authorityName.trim()) {
      const duplicate = authorityData.some(
        (item) =>
          item.authorityName.toLowerCase() === authorityName.trim().toLowerCase() &&
          (!isEditMode || item.id !== editId),
      );
      if (duplicate) {
        setErrors((prev) => ({ ...prev, authorityName: "An Authority with this name already exists." }));
      } else {
        setErrors((prev) => ({
          ...prev,
          authorityName: prev.authorityName === "An Authority with this name already exists." ? "" : prev.authorityName,
        }));
      }
    }
  }, [authorityName, authorityData, isEditMode, editId]);

  useEffect(() => {
    validateDuplicatesLive();
  }, [validateDuplicatesLive]);

 const handleAuthorityNameChange = (e) => {
    const value = e.target.value;
    setAuthorityName(value);
    const trimmedValue = value.trim();

    if (/^\s+$/.test(value)) {
        setErrors((prev) => ({ ...prev, authorityName: "Authority Name cannot be only spaces." }));
        return;
    }
    if (!trimmedValue) {
        setErrors((prev) => ({ ...prev, authorityName: "" }));
        return;
    }
    if (/^\d+$/.test(trimmedValue)) {
        setErrors((prev) => ({ ...prev, authorityName: "Authority Name cannot be only numeric." }));
        return;
    }
    if (/^[^a-zA-Z0-9]+$/.test(trimmedValue)) {
        setErrors((prev) => ({ ...prev, authorityName: "Authority Name cannot be only special characters." }));
        return;
    }
    if (
        /^[^a-zA-Z]*$/.test(trimmedValue) &&
        /[0-9]/.test(trimmedValue) &&
        /[^a-zA-Z0-9]/.test(trimmedValue)
    ) {
        setErrors((prev) => ({ ...prev, authorityName: "Authority Name cannot be only numbers and special characters." }));
        return;
    }
    if (trimmedValue.length > 100) {
        setErrors((prev) => ({ ...prev, authorityName: "Authority Name cannot exceed 100 characters." }));
        return;
    }
    setErrors((prev) => ({ ...prev, authorityName: "" }));
};

  const handleUserChange = (e) => {
    const value = e.target.value;

    setAuthorizedUserId(value);

    if (!value) {
        setErrors((prev) => ({
            ...prev,
            authorizedUserId: "Authorized User is required.",
        }));
        return;
    }

    setErrors((prev) => ({
        ...prev,
        authorizedUserId: "",
    }));
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (/^\s+$/.test(authorityName)) {
    newErrors.authorityName = "Authority Name cannot be only spaces.";
} else if (!authorityName.trim()) {
    newErrors.authorityName = "Authority Name is required.";
} else if (/^\d+$/.test(authorityName.trim())) {
    newErrors.authorityName = "Authority Name cannot be only numeric.";
} else if (/^[^a-zA-Z0-9]+$/.test(authorityName.trim())) {
    newErrors.authorityName = "Authority Name cannot be only special characters.";
} else if (
    /^[^a-zA-Z]*$/.test(authorityName.trim()) &&
    /[0-9]/.test(authorityName.trim()) &&
    /[^a-zA-Z0-9]/.test(authorityName.trim())
) {
    newErrors.authorityName = "Authority Name cannot be only numbers and special characters.";
} else if (authorityName.trim().length > 100) {
    newErrors.authorityName = "Authority Name cannot exceed 100 characters.";
} else {
    const duplicate = authorityData.some(
        (item) =>
            item.authorityName.toLowerCase() === authorityName.trim().toLowerCase() &&
            (!isEditMode || item.id !== editId),
    );
    if (duplicate) newErrors.authorityName = "An Authority with this name already exists.";
}
if (!authorizedUserId) {
    newErrors.authorizedUserId = "Authorized User is required.";
}
  setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // ---- NEW: only added, nothing else touched ----
    if (isEditMode && originalValues) {
      const noChanges =
        authorityName.trim() === originalValues.authorityName.trim() &&
        String(authorizedUserId) === String(originalValues.authorizedUserId) &&
        status === originalValues.status;

      if (noChanges) {
        toast.warning("No changes detected.");
        return;
      }
    }
    // ---- END NEW ----

    const requestBody = { authorityName: authorityName.trim(), status };

    setIsSubmitting(true);
    try {
      if (isEditMode) {
        const response = await updateAuthority(editId, authorizedUserId, requestBody, token);
        if (response && response.status === 200) {
          toast.success("Authority updated successfully!");
          await fetchAuthorities();
          handleCancel();
        } else {
          toast.error("Failed to update Authority.");
        }
      } else {
        const response = await createAuthority(authorizedUserId, requestBody, token);
        if (response && response.status === 201) {
          toast.success("Authority created successfully!");
          await fetchAuthorities();
          handleCancel();
        } else {
          toast.error("Failed to create Authority.");
        }
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        toast.error("You do not have permission to perform this action.");
      } else {
        toast.error(
          error?.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "create"} Authority. Please try again.`,
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (authority) => {
    if (authority.linkedEntriesCount > 0) {
      setPendingEditAuthority(authority);
      setShowEditWarningModal(true);
    } else {
      applyEdit(authority);
    }
  };

  const applyEdit = (authority) => {
  setIsEditMode(true);
  setEditId(authority.id);
  setAuthorityName(authority.authorityName);
  setAuthorizedUserId(String(authority.authorizedUserId));
  setStatus(authority.active);
  setErrors({});
  setOriginalValues({
    authorityName: authority.authorityName,
    authorizedUserId: String(authority.authorizedUserId),
    status: authority.active,
  });
  window.scrollTo({ top: 0, behavior: "smooth" });
};

  const confirmEditWithWarning = () => {
    setShowEditWarningModal(false);
    applyEdit(pendingEditAuthority);
    setPendingEditAuthority(null);
  };

 const handleCancel = () => {
  setAuthorityName("");
  setAuthorizedUserId("");
  setStatus(true);
  setIsEditMode(false);
  setEditId(null);
  setErrors({});
  setOriginalValues(null);
};

  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  };
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handleExportCSV = () => {
    const headers = ["Authority Name", "Authorized User", "Status"];
    const csvData = filteredData.map((item) => [
      item.authorityName,
      item.authorizedUserFullName || "",
      item.active ? "Active" : "Inactive",
    ]);
    let csv = headers.join(",") + "\n";
    csvData.forEach((row) => { csv += row.map((cell) => `"${cell}"`).join(",") + "\n"; });
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `authority-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const toggleStyle = {
    wrapper: { display: "flex", alignItems: "center", gap: "10px", marginTop: "8px" },
    label: { fontSize: "14px", color: "#555", minWidth: "52px" },
  };

  return (
    <Container fluid className="p-4">
      <style>{`
        .leave-toggle .form-check-input:checked {
          background-color: #2E5CB8 !important;
          border-color: #2E5CB8 !important;
        }
        .leave-toggle .form-check-input:focus {
          border-color: #2E5CB8 !important;
          box-shadow: 0 0 0 0.1rem rgba(46, 92, 184, 0.35) !important;
        }
        .lc-breadcrumb a { text-decoration: none !important; color: #2E5CB8 !important; }
        .lc-breadcrumb .breadcrumb-item.active { color: #6c757d !important; }
        .pagination .page-item.active .page-link {
          background-color: #2E5CB8 !important;
          border-color: #2E5CB8 !important;
        }
      `}</style>
<h4 className="page-main-heading" style={{ marginBottom: 12 }}>
        Authority
      </h4>
      <Breadcrumb className="lc-breadcrumb">
        <Breadcrumb.Item href="#">Leave</Breadcrumb.Item>
        <Breadcrumb.Item href="#">Shift & Authorities</Breadcrumb.Item>
        <Breadcrumb.Item active>Authority</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="mb-4">
        <Col xl={12}>
          <Card>
            <Card.Body>
              <Form>
                <Row>
                  <Col>
                    <Row className="mb-3">
                     <Form.Label className="col-sm-3 col-form-label form-label" style={{ whiteSpace: "nowrap" }}>
  Authority Name<span className="text-danger">*</span>
</Form.Label>
                      <Col sm={9}>
                        <Form.Control
                          type="text"
                          placeholder="Enter Authority Name"
                          value={authorityName}
                          onChange={handleAuthorityNameChange}
                        />
                        {errors.authorityName && (
                          <p style={{ color: "red", fontSize: "13px", marginTop: "3px" }}>{errors.authorityName}</p>
                        )}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Form.Label className="col-sm-3 col-form-label form-label">Active</Form.Label>
                      <Col sm={9}>
                        <div style={toggleStyle.wrapper} className="leave-toggle">
                          <Form.Check
                            type="switch"
                            id="status-toggle"
                            checked={status}
                            onChange={(e) => setStatus(e.target.checked)}
                          />
                          <span style={toggleStyle.label}>Check if Active</span>
                        </div>
                      </Col>
                    </Row>
                  </Col>

                  <Col>
                    <Row className="mb-3">
                      <Form.Label className="col-sm-3 col-form-label form-label" style={{ whiteSpace: "nowrap" }}>
  Authorized User<span className="text-danger">*</span>
</Form.Label>
                      <Col sm={9}>
                        <Form.Select value={authorizedUserId} onChange={handleUserChange}>
                          <option value="">Select Authorized User </option>
                          {users.map((user) => (
                            <option key={user.userInfoId} value={user.userInfoId}>
                              {user.name}
                            </option>
                          ))}
                        </Form.Select>
                        {errors.authorizedUserId && (
                          <p style={{ color: "red", fontSize: "13px", marginTop: "3px" }}>{errors.authorizedUserId}</p>
                        )}
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <Row className="align-items-center">
                  <Col className="mt-4 d-flex align-items-center justify-content-center">
                    <Button
                      variant="primary"
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      style={{ backgroundColor: "#2E5CB8", borderColor: "#2E5CB8" }}
                    >
                      {isSubmitting ? (isEditMode ? "Updating..." : "Submitting...") : (isEditMode ? "Update" : "Submit")}
                    </Button>
                    <Button
                      variant="secondary"
                      type="reset"
                      style={{ marginLeft: "10px" }}
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xl={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Authority List</h5>
              <Button variant="outline-primary" size="sm" onClick={handleExportCSV}>
                <i className="bi bi-download me-2"></i>Export CSV
              </Button>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3 justify-content-end align-items-center">
                <Col xs="auto">
                  <Form.Control
                    type="text"
                    placeholder="Search by Authority Name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ width: "200px", fontSize: "13px", height: "32px", padding: "4px 10px" }}
                  />
                </Col>
                <Col xs="auto">
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ width: "130px", fontSize: "13px", height: "32px", padding: "4px 10px" }}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Col>
                <Col xs="auto">
                  <Form.Select
                    value={sortField}
                    onChange={(e) => setSortField(e.target.value)}
                    style={{ width: "150px", fontSize: "13px", height: "32px", padding: "4px 10px" }}
                  >
                    <option value="name">Sort: Name (A–Z)</option>
                    <option value="status">Sort: Status</option>
                  </Form.Select>
                </Col>
                <Col xs="auto">
                  <Badge bg="secondary">Total: {filteredData.length}</Badge>
                </Col>
              </Row>

              <div className="table-responsive">
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "55px", textAlign: "center" }}>Sr.No</th>
                      <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Authority Name</th>
                      <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Authorized User</th>
                      <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "100px" }}>Status</th>
                      <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "90px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4">
                          <div className="text-muted">
                            <i className="bi bi-hourglass-split fs-3 d-block mb-2"></i>Loading...
                          </div>
                        </td>
                      </tr>
                    ) : getPaginatedData().length > 0 ? (
                      getPaginatedData().map((authority, index) => (
                        <tr key={authority.id}>
                          <td style={{ textAlign: "center" }}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                          <td>{authority.authorityName || "-"}</td>
                          <td>{authority.authorizedUserFullName || "-"}</td>
                          <td><span style={{ fontWeight: 500 }}>{authority.active ? "Active" : "Inactive"}</span></td>
                          <td>
                            <button
                              className="btn btn-secondary"
                              type="button"
                              style={{ padding: "3px 8px", fontSize: "12px" }}
                              onClick={() => handleEdit(authority)}
                            >
                              <i className="bi bi-pencil-fill me-1" />Edit
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-4">
                          <div className="text-muted">
                            <i className="bi bi-inbox fs-3 d-block mb-2"></i>No authorities found
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-end">
                  <Pagination>
                    <Pagination.Prev onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 1} />
                    {[...Array(totalPages).keys()].map((n) => (
                      <Pagination.Item key={n + 1} active={n + 1 === currentPage} onClick={() => setCurrentPage(n + 1)}>
                        {n + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next onClick={() => setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showEditWarningModal} onHide={() => setShowEditWarningModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />Edit Authority
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{pendingEditAuthority?.authorityName}</strong> is linked to{" "}
            <strong>
              {pendingEditAuthority?.linkedEntriesCount}{" "}
              {pendingEditAuthority?.linkedEntriesCount === 1 ? "entry" : "entries"}
            </strong>.
          </p>
          <p className="mb-0 text-muted">
            Any changes to the authority name will propagate to all associated entries. Do you want to proceed?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditWarningModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={confirmEditWithWarning} style={{ backgroundColor: "#2E5CB8", borderColor: "#2E5CB8" }}>
            Yes, Edit
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer position="top-right" autoClose={3000} />
    </Container>
  );
};

export default Authority;
